import React from "react";
import { LifeBuoy, ExternalLink } from "lucide-react";
import { STATES, STATE_RESOURCES } from "@/lib/states";

/**
 * FreeTenantHelp
 * --------------
 * Reusable directory of Australia's free state-run tenant services (Tenants'
 * Unions, RTA/Fair Trading, tribunals). Same data that lives in the footer
 * accordion, surfaced contextually next to AI-generated answers, on Q&A
 * pages and on the Support page.
 *
 * Modes:
 *   variant="full"    → full 8-state grid (used at bottom of /q index, Support page)
 *   variant="single"  → compact block for one state code (used on /q/[state]/[slug])
 */
export default function FreeTenantHelp({
  variant = "full",
  state = null,
  heading = null,
  subheading = null,
}) {
  if (variant === "single") {
    const stateMeta = STATES.find((s) => s.code === state);
    const links = STATE_RESOURCES[state] || [];
    if (!stateMeta || links.length === 0) return null;
    return (
      <aside
        className="border-l-4 border-l-[#1B6C62] bg-[#F1F7F5] px-5 py-4 my-8"
        data-testid={`free-tenant-help-single-${state}`}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <LifeBuoy size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62] font-semibold">
            Free help in {stateMeta.name}
          </span>
        </div>
        <p className="text-[13px] text-[#1A1A1A] font-serif leading-relaxed mb-2">
          Not legal advice. If your situation is serious or time-critical, contact one of these free {stateMeta.name} services:
        </p>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] font-serif">
          {links.map(([name, url]) => (
            <li key={url}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0D2B52] hover:text-[#1B6C62] underline underline-offset-2 inline-flex items-center gap-1"
                data-testid={`free-help-link-${state}-${name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {name} <ExternalLink size={11} strokeWidth={1.75} />
              </a>
            </li>
          ))}
        </ul>
      </aside>
    );
  }

  // Full grid variant
  return (
    <section
      className="border-t border-[#E5E4DE] pt-10 mt-12"
      data-testid="free-tenant-help-full"
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 label-mono mb-3 text-[#1B6C62]">
          <LifeBuoy size={13} strokeWidth={1.75} /> Free help in your state
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] mb-2">
          {heading || "Still stuck? These services are free."}
        </h2>
        <p className="text-[14px] text-[#6B7280] max-w-xl mx-auto font-serif">
          {subheading ||
            "Compass and the Tenant Brain are AI. For advice on your specific matter, or if you're heading to a tribunal, these government-funded services in your state will help you free of charge."}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6">
        {STATES.map((s) => (
          <div key={s.code} data-testid={`free-help-state-${s.code}`}>
            <div className="font-serif text-[14px] text-[#0D2B52] mb-1.5">
              {s.name}{" "}
              <span className="font-mono text-[10px] text-[#6B7280]">({s.code})</span>
            </div>
            <ul className="space-y-1 text-[12.5px] text-[#5a6b7a]">
              {(STATE_RESOURCES[s.code] || []).map(([name, url]) => (
                <li key={url}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#1B6C62] hover:underline"
                  >
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
